import React from "react";
import api, { MEDIA } from "../../lib/api";
import { toast } from "sonner";
import { Send, Plus, X, Upload, Download, Sparkles, Package, Percent, Gift, Code, Eye, Users, Search, Heart } from "lucide-react";

const TEMPLATE_ICONS = {
    products_list: Package,
    single_product: Sparkles,
    discount_category: Percent,
    coupon: Gift,
    personalized_recommendations: Heart,
};

export default function AdminNewsletter() {
    const [subs, setSubs] = React.useState([]);
    const [templates, setTemplates] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [products, setProducts] = React.useState([]);

    const [mode, setMode] = React.useState("template");
    const [selectedTemplate, setSelectedTemplate] = React.useState(null);
    const [params, setParams] = React.useState({});
    const [productPickerOpen, setProductPickerOpen] = React.useState(false);
    const [productPickerMode, setProductPickerMode] = React.useState("multi");
    const [selectedProductIds, setSelectedProductIds] = React.useState([]);
    const [productSearch, setProductSearch] = React.useState("");
    const [productFilter, setProductFilter] = React.useState("all"); // all | sale | newest

    const [subject, setSubject] = React.useState("");
    const [html, setHtml] = React.useState("");
    const [rendering, setRendering] = React.useState(false);
    const [sending, setSending] = React.useState(false);

    // Selecție abonați
    const [recipientMode, setRecipientMode] = React.useState("all"); // "all" | "selected"
    const [selectedEmails, setSelectedEmails] = React.useState(new Set());
    const [recipientSearch, setRecipientSearch] = React.useState("");
    const [recipientPickerOpen, setRecipientPickerOpen] = React.useState(false);

    // Filtru pentru lista de abonați
    const [subsFilter, setSubsFilter] = React.useState("active"); // active | inactive | all

    React.useEffect(() => {
        api.get("/admin/newsletter").then(r => setSubs(r.data || []));
        api.get("/admin/newsletter/templates").then(r => setTemplates(r.data || []));
        api.get("/admin/newsletter/categories").then(r => setCategories(r.data || []));
        api.get("/admin/products").then(r => setProducts(r.data || []));
    }, []);

    const selectTemplate = (tpl) => {
        setSelectedTemplate(tpl);
        setParams({});
        setSelectedProductIds([]);
        setSubject("");
        setHtml("");
    };

    const setParam = (key, value) => setParams(p => ({ ...p, [key]: value }));

    const openProductPicker = (singleMode = false) => {
        setProductPickerMode(singleMode ? "single" : "multi");
        setProductPickerOpen(true);
    };

    const toggleProduct = (id) => {
        if (productPickerMode === "single") {
            setSelectedProductIds([id]);
            setParam("product_id", id);
            setProductPickerOpen(false);
        } else {
            setSelectedProductIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
        }
    };

    const confirmMultiProducts = () => {
        setParam("product_ids", selectedProductIds);
        setProductPickerOpen(false);
    };

    const renderTemplate = async () => {
        if (!selectedTemplate) return;
        setRendering(true);
        try {
            const res = await api.post("/admin/newsletter/render", {
                template_type: selectedTemplate.id,
                params,
            });
            setSubject(res.data.subject || "");
            setHtml(res.data.html || "");
            toast.success("Template generat cu succes");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Eroare la generare");
        }
        setRendering(false);
    };

    // Abonați helpers — doar abonații activi pot primi newsletter
    const activeSubs = React.useMemo(() => subs.filter(s => s.active !== false), [subs]);
    const toggleRecipient = (email) => {
        const ns = new Set(selectedEmails);
        if (ns.has(email)) ns.delete(email); else ns.add(email);
        setSelectedEmails(ns);
    };
    const selectAllRecipients = () => setSelectedEmails(new Set(activeSubs.map(s => s.email)));
    const deselectAllRecipients = () => setSelectedEmails(new Set());
    const filteredSubs = activeSubs.filter(s =>
        !recipientSearch || s.email.toLowerCase().includes(recipientSearch.toLowerCase())
    );

    const recipientsCount = recipientMode === "all" ? activeSubs.length : selectedEmails.size;

    const send = async () => {
        if (!subject || !html) {
            toast.error("Generează întâi conținutul (Generează template)");
            return;
        }
        if (recipientsCount === 0) {
            toast.error("Nu ai selectat niciun destinatar");
            return;
        }
        if (!confirm(`Trimit newsletter la ${recipientsCount} abonați. Continui?`)) return;
        setSending(true);
        try {
            const body = { subject, html };
            if (recipientMode === "selected") {
                body.recipient_emails = Array.from(selectedEmails);
            }
            if (selectedTemplate?.id === "personalized_recommendations") {
                body.max_products = parseInt(params.max_products) || 4;
            }
            const res = await api.post("/admin/newsletter/send-campaign", body);
            if (res.data.ok) {
                toast.success(`Trimis: ${res.data.sent}/${res.data.total}${res.data.failed ? ` (${res.data.failed} eșuate)` : ""}`);
            } else {
                toast.error(res.data.error || "Eroare SMTP");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Eroare la trimitere");
        }
        setSending(false);
    };

    const selectedProducts = products.filter(p => selectedProductIds.includes(p.product_id));
    const pickedSingleProduct = products.find(p => p.product_id === params.product_id);

    // Abonați vizibili în panoul din dreapta
    const visibleSubs = React.useMemo(() => {
        if (subsFilter === "active") return subs.filter(s => s.active !== false);
        if (subsFilter === "inactive") return subs.filter(s => s.active === false);
        return subs;
    }, [subs, subsFilter]);

    const reactivateSub = async (email) => {
        try {
            await api.post("/admin/newsletter/reactivate", { email });
            const r = await api.get("/admin/newsletter");
            setSubs(r.data || []);
            toast.success(`${email} a fost reactivat`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare la reactivare");
        }
    };

    const deleteSub = async (email) => {
        if (!window.confirm(`Șterge definitiv ${email} din baza de date? (GDPR right-to-be-forgotten)`)) return;
        try {
            await api.delete(`/admin/newsletter/${encodeURIComponent(email)}`);
            setSubs(s => s.filter(x => x.email !== email));
            setSelectedEmails(s => {
                const next = new Set(s);
                next.delete(email);
                return next;
            });
            toast.success(`${email} șters`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare la ștergere");
        }
    };

    // Filtre pentru product picker
    const filteredPickerProducts = React.useMemo(() => {
        let list = [...products];
        // Filtrare după criteriul ales
        if (productFilter === "sale") {
            list = list.filter(p => p.sale_price && p.sale_price < p.price);
        } else if (productFilter === "newest") {
            list = list.sort((a, b) => {
                const da = new Date(a.created_at || 0).getTime();
                const db = new Date(b.created_at || 0).getTime();
                return db - da;
            }).slice(0, 30);
        }
        // Search după nume (case-insensitive, diacritics-insensitive)
        if (productSearch) {
            const q = productSearch.toLowerCase()
                .replace(/ă/g, "a").replace(/â/g, "a").replace(/î/g, "i")
                .replace(/ș/g, "s").replace(/ț/g, "t");
            list = list.filter(p => {
                const name = (p.name || "").toLowerCase()
                    .replace(/ă/g, "a").replace(/â/g, "a").replace(/î/g, "i")
                    .replace(/ș/g, "s").replace(/ț/g, "t");
                const sku = (p.sku || "").toLowerCase();
                return name.includes(q) || sku.includes(q);
            });
        }
        return list;
    }, [products, productFilter, productSearch]);

    return (
        <div data-testid="admin-newsletter">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                <div>
                    <div className="overline mb-1">Administrare</div>
                    <h1 className="font-heading text-3xl">Newsletter ({activeSubs.length} activi / {subs.length} total)</h1>
                </div>
                <div className="flex gap-2">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/export`}
                        target="_blank" rel="noopener noreferrer"
                        className="btn-outline flex items-center gap-2 text-sm">
                        <Download className="h-4 w-4" /> Export CSV
                    </a>
                    <label className="btn-outline flex items-center gap-2 text-sm cursor-pointer">
                        <Upload className="h-4 w-4" /> Import CSV
                        <input type="file" accept=".csv" className="hidden"
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const fd = new FormData(); fd.append("file", file);
                                try {
                                    const res = await api.post("/admin/newsletter/import", fd,
                                        { headers: { "Content-Type": "multipart/form-data" } });
                                    toast.success(`Import: ${res.data.created} adăugați, ${res.data.updated} actualizați, ${res.data.skipped} omiși`);
                                    api.get("/admin/newsletter").then(r => setSubs(r.data || []));
                                } catch (err) { toast.error(err.response?.data?.detail || "Eroare la import"); }
                                e.target.value = "";
                            }} />
                    </label>
                </div>
            </div>

            <div className="flex gap-2 mb-4">
                <button
                    onClick={() => setMode("template")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${mode === "template" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    <Sparkles className="h-4 w-4 inline mr-2" />
                    Template-uri predefinite
                </button>
                <button
                    onClick={() => setMode("custom")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${mode === "custom" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    <Code className="h-4 w-4 inline mr-2" />
                    HTML personalizat
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                    {mode === "template" && (
                        <>
                            <div className="card-soft p-5">
                                <h2 className="font-heading text-xl mb-3">1. Alege tipul de newsletter</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {templates.map(tpl => {
                                        const Icon = TEMPLATE_ICONS[tpl.id] || Sparkles;
                                        const active = selectedTemplate?.id === tpl.id;
                                        return (
                                            <button key={tpl.id} onClick={() => selectTemplate(tpl)}
                                                className={`text-left p-4 rounded-xl border-2 transition ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                                                <div className="flex items-start gap-3">
                                                    <div className={`p-2 rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                                                        <Icon className="h-5 w-5" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-semibold">{tpl.label}</div>
                                                        <div className="text-xs text-muted-foreground mt-1">{tpl.description}</div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {selectedTemplate && (
                                <div className="card-soft p-5">
                                    <h2 className="font-heading text-xl mb-3">2. Completează parametrii</h2>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Subiect email</label>
                                            <input className="input-soft" placeholder="(opțional — se completează din titlu)"
                                                value={params.subject || ""} onChange={e => setParam("subject", e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Titlu principal</label>
                                            <input className="input-soft"
                                                placeholder={
                                                    selectedTemplate.id === "discount_category" ? "ex: Reduceri de toamnă" :
                                                    selectedTemplate.id === "single_product" ? "ex: Produsul săptămânii" :
                                                    selectedTemplate.id === "coupon" ? "ex: 15% OFF pentru tine" :
                                                    "ex: Recomandările noastre"
                                                }
                                                value={params.title || ""} onChange={e => setParam("title", e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Mesaj introductiv</label>
                                            <textarea rows="2" className="input-soft" placeholder="Text scurt sub titlu..."
                                                value={params.intro || ""} onChange={e => setParam("intro", e.target.value)} />
                                        </div>

                                        {selectedTemplate.id === "products_list" && (
                                            <div>
                                                <label className="text-sm font-medium mb-1 block">Produse de inclus</label>
                                                <button onClick={() => openProductPicker(false)} className="btn-outline w-full flex items-center justify-center gap-2">
                                                    <Plus className="h-4 w-4" />
                                                    {selectedProducts.length > 0 ? `${selectedProducts.length} produse selectate` : "Alege produse din catalog"}
                                                </button>
                                                {selectedProducts.length > 0 && (
                                                    <div className="mt-2 text-xs text-muted-foreground">
                                                        {selectedProducts.map(p => p.name).join(", ")}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {selectedTemplate.id === "single_product" && (
                                            <div>
                                                <label className="text-sm font-medium mb-1 block">Produs</label>
                                                <button onClick={() => openProductPicker(true)} className="btn-outline w-full flex items-center justify-center gap-2">
                                                    <Plus className="h-4 w-4" />
                                                    {pickedSingleProduct ? pickedSingleProduct.name : "Alege un produs"}
                                                </button>
                                            </div>
                                        )}

                                        {selectedTemplate.id === "discount_category" && (
                                            <>
                                                <div>
                                                    <label className="text-sm font-medium mb-1 block">Categorie</label>
                                                    <select className="input-soft" value={params.category_slug || ""}
                                                        onChange={e => setParam("category_slug", e.target.value)}>
                                                        <option value="">-- alege categoria --</option>
                                                        {categories.map(c => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
                                                    </select>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-sm font-medium mb-1 block">Procent reducere</label>
                                                        <div className="relative">
                                                            <input type="number" min="1" max="99" className="input-soft pr-8" placeholder="10"
                                                                value={params.discount_percent || ""} onChange={e => setParam("discount_percent", e.target.value)} />
                                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium mb-1 block">Max. produse afișate</label>
                                                        <input type="number" min="1" max="12" className="input-soft" placeholder="6"
                                                            value={params.max_products || ""} onChange={e => setParam("max_products", e.target.value)} />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-sm font-medium mb-1 block">Cod cupon (opțional)</label>
                                                        <input className="input-soft uppercase" placeholder="AUTOMN10"
                                                            value={params.coupon_code || ""} onChange={e => setParam("coupon_code", e.target.value.toUpperCase())} />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium mb-1 block">Valabil până la</label>
                                                        <input type="date" className="input-soft" value={params.valid_until || ""}
                                                            onChange={e => setParam("valid_until", e.target.value)} />
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {selectedTemplate.id === "coupon" && (
                                            <>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-sm font-medium mb-1 block">Cod cupon *</label>
                                                        <input className="input-soft uppercase font-mono" placeholder="WELCOME20"
                                                            value={params.coupon_code || ""} onChange={e => setParam("coupon_code", e.target.value.toUpperCase())} />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium mb-1 block">Procent reducere</label>
                                                        <div className="relative">
                                                            <input type="number" min="1" max="99" className="input-soft pr-8" placeholder="20"
                                                                value={params.discount_percent || ""} onChange={e => setParam("discount_percent", e.target.value)} />
                                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium mb-1 block">Valabil până la</label>
                                                    <input type="date" className="input-soft" value={params.valid_until || ""}
                                                        onChange={e => setParam("valid_until", e.target.value)} />
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-sm font-medium mb-1 block">Text buton CTA</label>
                                                        <input className="input-soft" placeholder="Cumpără acum"
                                                            value={params.cta_text || ""} onChange={e => setParam("cta_text", e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium mb-1 block">Link CTA (opțional)</label>
                                                        <input className="input-soft" placeholder="/magazin sau URL complet"
                                                            value={params.cta_url || ""} onChange={e => setParam("cta_url", e.target.value)} />
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {selectedTemplate.id === "personalized_recommendations" && (
                                            <div className="space-y-3">
                                                <div className="bg-muted/50 border border-border rounded-lg p-3 text-xs text-muted-foreground leading-relaxed">
                                                    <Heart className="h-3.5 w-3.5 inline -mt-0.5 mr-1 text-primary" />
                                                    <strong>Cum funcționează:</strong> la trimitere, backendul inspectează ultimele comenzi ale fiecărui abonat și inserează produse din aceleași categorii (excluzând cele deja cumpărate). Pentru abonații fără istoric, se folosesc cele mai recente produse active.
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium mb-1 block">Număr produse per destinatar</label>
                                                    <input className="input-soft" type="number" min="2" max="8" placeholder="4"
                                                        value={params.max_products || ""} onChange={e => setParam("max_products", parseInt(e.target.value) || 4)} data-testid="personalized-max-products" />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <button onClick={renderTemplate} disabled={rendering}
                                        className="btn-primary mt-4 w-full flex items-center justify-center gap-2">
                                        <Eye className="h-4 w-4" />
                                        {rendering ? "Se generează..." : "Generează newsletter"}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {mode === "custom" && (
                        <div className="card-soft p-5 space-y-3">
                            <h2 className="font-heading text-xl">HTML personalizat</h2>
                            <input value={subject} onChange={(e) => setSubject(e.target.value)}
                                placeholder="Subiect email" className="input-soft" />
                            <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows="18"
                                className="input-soft font-mono text-xs" placeholder="<html>...</html>" />
                        </div>
                    )}

                    {/* Preview + Recipient selector + Send */}
                    {(html || subject) && (
                        <div className="card-soft p-5">
                            <h2 className="font-heading text-xl mb-3">3. Destinatari & trimitere</h2>

                            <div className="mb-3">
                                <label className="text-sm font-medium mb-1 block">Subiect final</label>
                                <input className="input-soft" value={subject} onChange={e => setSubject(e.target.value)} />
                            </div>

                            {/* Recipient mode */}
                            <div className="mb-4 p-4 bg-muted rounded-xl">
                                <div className="flex items-center gap-2 mb-3">
                                    <Users className="h-4 w-4" />
                                    <div className="text-sm font-semibold">Cui trimit?</div>
                                </div>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    <button onClick={() => setRecipientMode("all")}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${recipientMode === "all" ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                                        Toți abonații activi ({activeSubs.length})
                                    </button>
                                    <button onClick={() => { setRecipientMode("selected"); setRecipientPickerOpen(true); }}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${recipientMode === "selected" ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                                        Selectare manuală ({selectedEmails.size})
                                    </button>
                                </div>

                                {recipientMode === "selected" && (
                                    <div>
                                        <button onClick={() => setRecipientPickerOpen(true)}
                                            className="text-xs font-medium text-primary hover:underline">
                                            + Modifică selecția ({selectedEmails.size} selectați)
                                        </button>
                                        {selectedEmails.size > 0 && selectedEmails.size <= 5 && (
                                            <div className="mt-2 text-xs text-muted-foreground">
                                                {Array.from(selectedEmails).join(", ")}
                                            </div>
                                        )}
                                        {selectedEmails.size > 5 && (
                                            <div className="mt-2 text-xs text-muted-foreground">
                                                {Array.from(selectedEmails).slice(0, 3).join(", ")} și alți {selectedEmails.size - 3}...
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <div className="text-sm font-medium mb-1">Preview email:</div>
                                <iframe title="preview" srcDoc={html}
                                    className="w-full h-96 border border-border rounded-xl bg-white" />
                            </div>

                            <button onClick={send} disabled={sending || !html || recipientsCount === 0}
                                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                                <Send className="h-4 w-4" />
                                {sending
                                    ? "Se trimite..."
                                    : `Trimite la ${recipientsCount} ${recipientsCount === 1 ? "abonat" : "abonați"}`}
                            </button>
                            <p className="text-xs text-muted-foreground mt-2 text-center">
                                Necesită SMTP configurat în Setări. Culorile sunt preluate din tema site-ului.
                            </p>
                        </div>
                    )}
                </div>

                {/* Lista abonați cu status + acțiuni */}
                <div className="card-soft p-5">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h2 className="font-heading text-xl">Abonați ({subs.filter(s => s.active !== false).length} activi / {subs.length} total)</h2>
                        <div className="flex items-center gap-1 text-xs">
                            {[
                                { id: "active", label: "Activi" },
                                { id: "inactive", label: "Dezabonați" },
                                { id: "all", label: "Toți" },
                            ].map(f => (
                                <button
                                    key={f.id}
                                    onClick={() => setSubsFilter(f.id)}
                                    className={`px-2.5 py-1 rounded-full border transition ${subsFilter === f.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                                    data-testid={`subs-filter-${f.id}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="max-h-[700px] overflow-y-auto divide-y divide-border">
                        {visibleSubs.length === 0 && <div className="text-sm text-muted-foreground py-4">Niciun abonat în această categorie.</div>}
                        {visibleSubs.map(s => {
                            const isActive = s.active !== false;
                            return (
                                <div key={s.email} className="py-2.5 text-sm flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs truncate">{s.email}</span>
                                            {!isActive && (
                                                <span className="text-[9px] bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap">DEZABONAT</span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground">
                                            {isActive
                                                ? `Abonat: ${new Date(s.subscribed_at).toLocaleString("ro-RO")}`
                                                : `Dezabonat: ${s.unsubscribed_at ? new Date(s.unsubscribed_at).toLocaleString("ro-RO") : "—"}`}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {!isActive && (
                                            <button
                                                onClick={() => reactivateSub(s.email)}
                                                className="text-[10px] px-2 py-1 rounded-full bg-primary text-primary-foreground hover:opacity-90"
                                                data-testid={`reactivate-${s.email}`}
                                            >
                                                Reactivează
                                            </button>
                                        )}
                                        <button
                                            onClick={() => deleteSub(s.email)}
                                            className="text-[10px] px-2 py-1 rounded-full border border-border text-muted-foreground hover:text-destructive hover:border-destructive"
                                            data-testid={`delete-${s.email}`}
                                            title="Șterge complet (GDPR)"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Modal: Product picker */}
            {productPickerOpen && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <h2 className="font-heading text-xl">
                                {productPickerMode === "single" ? "Alege un produs" : `Alege produse (${selectedProductIds.length} selectate)`}
                            </h2>
                            <button onClick={() => setProductPickerOpen(false)} data-testid="product-picker-close"><X className="h-5 w-5" /></button>
                        </div>
                        {/* Bara de căutare + filtre */}
                        <div className="px-5 pt-4 pb-3 border-b border-border bg-muted/40">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <input
                                        type="text"
                                        value={productSearch}
                                        onChange={(e) => setProductSearch(e.target.value)}
                                        placeholder="Caută după nume sau SKU..."
                                        className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                                        data-testid="product-search-input"
                                        autoFocus
                                    />
                                </div>
                                {productSearch && (
                                    <button onClick={() => setProductSearch("")} className="text-xs text-muted-foreground hover:text-foreground px-2" data-testid="product-search-clear">
                                        Șterge
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                                <span className="text-muted-foreground">Filtru:</span>
                                {[
                                    { id: "all", label: "Toate" },
                                    { id: "sale", label: "La reducere" },
                                    { id: "newest", label: "Ultimele adăugate" },
                                ].map(f => (
                                    <button
                                        key={f.id}
                                        onClick={() => setProductFilter(f.id)}
                                        className={`px-3 py-1 rounded-full border transition ${productFilter === f.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-background"}`}
                                        data-testid={`product-filter-${f.id}`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                                <span className="ml-auto text-muted-foreground">
                                    {filteredPickerProducts.length} / {products.length} produse
                                </span>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-2 md:grid-cols-3 gap-3">
                            {filteredPickerProducts.length === 0 && (
                                <div className="col-span-full text-center text-sm text-muted-foreground py-10">
                                    Niciun produs nu corespunde filtrelor curente.
                                </div>
                            )}
                            {filteredPickerProducts.map(p => {
                                const onSale = p.sale_price && p.sale_price < p.price;
                                return (
                                    <button type="button" key={p.product_id} onClick={() => toggleProduct(p.product_id)}
                                        className={`card-soft p-3 text-left hover:shadow-md transition relative ${selectedProductIds.includes(p.product_id) ? "ring-2 ring-primary" : ""}`}
                                        data-testid={`product-pick-${p.product_id}`}>
                                        {onSale && (
                                            <span className="absolute top-2 right-2 bg-destructive text-destructive-foreground text-[10px] px-2 py-0.5 rounded-full font-semibold">
                                                -{Math.round(100 - (p.sale_price / p.price) * 100)}%
                                            </span>
                                        )}
                                        <div className="aspect-square bg-muted rounded-lg mb-2 overflow-hidden">
                                            {p.images?.[0] && <img src={MEDIA(p.images[0])} alt="" className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="text-xs font-medium line-clamp-2">{p.name}</div>
                                        <div className="text-xs mt-1 font-semibold">
                                            {onSale ? (
                                                <>
                                                    <span className="text-primary">{p.sale_price.toFixed(2)} RON</span>
                                                    <span className="text-muted-foreground line-through ml-1 text-[10px]">{p.price.toFixed(2)}</span>
                                                </>
                                            ) : (
                                                <span className="text-primary">{p.price.toFixed(2)} RON</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        {productPickerMode === "multi" && (
                            <div className="p-4 border-t border-border flex justify-end gap-2">
                                <button onClick={() => setProductPickerOpen(false)} className="btn-outline">Anulează</button>
                                <button onClick={confirmMultiProducts} className="btn-primary">Confirmă ({selectedProductIds.length})</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal: Recipient picker */}
            {recipientPickerOpen && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <h2 className="font-heading text-xl">Alege destinatarii ({selectedEmails.size}/{activeSubs.length} activi)</h2>
                            <button onClick={() => setRecipientPickerOpen(false)}><X className="h-5 w-5" /></button>
                        </div>

                        <div className="p-4 border-b border-border">
                            <div className="relative mb-3">
                                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    className="input-soft pl-9"
                                    placeholder="Caută după email..."
                                    value={recipientSearch}
                                    onChange={e => setRecipientSearch(e.target.value)} />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={selectAllRecipients} className="btn-outline text-xs flex-1">Selectează toți ({activeSubs.length})</button>
                                <button onClick={deselectAllRecipients} className="btn-outline text-xs flex-1">Deselectează toți</button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {filteredSubs.length === 0 && <div className="p-5 text-sm text-muted-foreground text-center">Niciun abonat găsit.</div>}
                            {filteredSubs.map(s => (
                                <label key={s.email}
                                    className="flex items-center gap-3 p-3 border-b border-border hover:bg-muted cursor-pointer">
                                    <input type="checkbox" checked={selectedEmails.has(s.email)}
                                        onChange={() => toggleRecipient(s.email)}
                                        className="h-4 w-4 accent-primary" />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium">{s.email}</div>
                                        <div className="text-xs text-muted-foreground">
                                            Abonat {new Date(s.subscribed_at).toLocaleDateString("ro-RO")}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>

                        <div className="p-4 border-t border-border flex justify-end gap-2">
                            <button onClick={() => setRecipientPickerOpen(false)} className="btn-outline">Anulează</button>
                            <button onClick={() => setRecipientPickerOpen(false)} className="btn-primary">
                                Confirmă ({selectedEmails.size} selectați)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
